import type { AdminKey, ContentPart, ContentSegment, ContentUnavailable, KeySummary, LogKeyChoice, LogPage, LogRow, NewKey, OwnedServiceKey, PartBreakdown, RequestContent, RequestDetail, ServiceKey, SessionPage, SessionSummary, ToolDefinition, UsageReport, UserSummary } from "../../server/src/model";
import type { BusinessUnit, DirectoryEntry, Division, Taxonomy } from "../../server/src/directory";
import type { AuditEvent } from "../../server/src/audit";
import type { Forecast } from "../../server/src/forecast";
import type { Finding, SessionInsights } from "../../server/src/insights";
import type { SessionReview } from "../../server/src/review";
import type { HourBucket, SavingsEstimate, TopSession } from "../../server/src/home";
import type { Catalog, CatalogEntry, CatalogPrice } from "../../server/src/catalog";
export type { Catalog, CatalogEntry, CatalogPrice };
export type { Finding, Forecast, HourBucket, SavingsEstimate, SessionInsights, SessionReview, TopSession };

export type { ContentPart, ContentSegment, ContentUnavailable, PartBreakdown, RequestContent, ToolDefinition };
export type RequestContentResponse = RequestContent | ContentUnavailable;
export type { AdminKey, AuditEvent, BusinessUnit, DirectoryEntry, Division, KeySummary, LogKeyChoice, Taxonomy, LogPage, LogRow, NewKey, OwnedServiceKey, RequestDetail, ServiceKey, SessionPage, SessionSummary, UsageReport, UserSummary };

/** What an administrator fills in for a service key (TAI-2298). */
export interface ServiceKeyRequest {
  name: string;
  division: string;
  businessUnit: string;
  owner: string;
  /** A different person from the owner; both are required (key policy §4.6, TAI-2567). */
  backupOwner: string;
  /** A team in the service organization; "" for none. Leave it out to leave a key's team alone. */
  teamId?: string;
  models: string[];
  maxBudget: number | null;
  budgetDuration: "1d" | "7d" | "30d" | null;
}

/** The teams, as the service-key form needs them. `available` is false while the service key cannot list teams. */
export interface AdminTeams {
  available: boolean;
  teams: Array<{ id: string; alias: string | null; modelCount: number; assignable: boolean }>;
}

export interface Me {
  user: UserSummary | { userId: string; email: string | null };
  isAdmin: boolean;
  directory: DirectoryEntry | null;
  taxonomy: Taxonomy;
  mode: "live" | "snapshot";
  readOnly: boolean;
  gatewayUrl: string;
  devAuth: boolean;
  /** The on-demand AI review of a session is available (TAI-2271). */
  sessionReview: boolean;
}

/** The editable fields of a key; tags here are the EXTRA tags (division and business unit are fixed). */
export interface KeyFields {
  models: string[];
  maxBudget: number | null;
  budgetDuration: "1d" | "7d" | "30d" | null;
  tags: string[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      // Required by the BFF on every mutating call (CSRF defence alongside SameSite=Lax).
      ...(method !== "GET" ? { "x-keyportal-request": "1" } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, (data && data.error) || `${res.status}`);
  return data as T;
}

const qs = (p: Record<string, string | number | null | undefined>) =>
  new URLSearchParams(Object.entries(p).filter(([, v]) => v !== null && v !== undefined && v !== "") as [string, string][]).toString();

export const api = {
  me: () => request<Me>("GET", "/api/me"),
  devUsers: () => request<Array<{ userId: string; email: string | null; role: string }>>("GET", "/api/dev/users"),
  devSignIn: (userId: string) => request("POST", "/api/dev/session", { userId }),
  signOut: () => request<{ ok: boolean; logoutUrl: string | null }>("POST", "/api/session/logout"),
  authMethods: () => request<{ oidc: boolean; dev: boolean }>("GET", "/api/auth/methods"),
  onboard: (division: string, businessUnit: string) => request<DirectoryEntry>("POST", "/api/me/directory", { division, businessUnit }),
  models: () => request<string[]>("GET", "/api/models"),
  catalog: () => request<Catalog>("GET", "/api/catalog"),
  keys: () => request<KeySummary[]>("GET", "/api/keys"),
  createKey: (name: string, f: KeyFields) => request<NewKey>("POST", "/api/keys", { name, ...f }),
  updateKey: (id: string, f: KeyFields) => request<KeySummary>("PATCH", `/api/keys/${id}`, f),
  rotateKey: (id: string) => request<NewKey>("POST", `/api/keys/${id}/rotate`),
  revokeKey: (id: string) => request<{ revoking: boolean }>("DELETE", `/api/keys/${id}`),
  /** The service keys this person owns or backs up (TAI-2568): view, rotate and revoke only. */
  myServiceKeys: () => request<OwnedServiceKey[]>("GET", "/api/service-keys"),
  rotateMyServiceKey: (id: string) => request<{ key: string; summary: OwnedServiceKey }>("POST", `/api/service-keys/${id}/rotate`),
  revokeMyServiceKey: (id: string) => request<{ revoking: boolean }>("DELETE", `/api/service-keys/${id}`),
  forecast: () => request<Forecast>("GET", "/api/usage/forecast"),
  usage: (days: number) => request<UsageReport>("GET", `/api/usage?${qs({ days })}`),
  /** The keys this person may filter the request log by (TAI-2570). */
  logKeys: () => request<LogKeyChoice[]>("GET", "/api/log-keys"),
  logs: (p: { days: number; page: number; pageSize: number; model?: string; status?: string; userId?: string; keyId?: string }, admin = false) =>
    request<LogPage>("GET", `/api/${admin ? "admin/" : ""}logs?${qs(p)}`),
  logDetail: (requestId: string) => request<RequestDetail>("GET", `/api/logs/${encodeURIComponent(requestId)}`),
  content: (requestId: string) => request<RequestContent | ContentUnavailable>("GET", `/api/logs/${encodeURIComponent(requestId)}/content`),
  contentItem: (requestId: string, kind: "segment" | "tool" | "response" | "thinking", index: number) =>
    request<{ text: string }>("GET", `/api/logs/${encodeURIComponent(requestId)}/content/${kind}/${index}`),
  sessions: (p: { days: number; page: number; pageSize: number; model?: string; userId?: string; keyId?: string }, admin = false) =>
    request<SessionPage>("GET", `/api/sessions?${qs({ ...p, scope: admin ? "all" : undefined })}`),
  sessionRequests: (sessionId: string, keyId: string) =>
    request<RequestDetail[]>("GET", `/api/sessions/requests?${qs({ sessionId, keyId })}`),
  sessionInsights: (sessionId: string, keyId: string) =>
    request<SessionInsights>("GET", `/api/sessions/insights?${qs({ sessionId, keyId })}`),
  sessionReview: (sessionId: string, keyId: string) => request<SessionReview>("POST", "/api/sessions/review", { sessionId, keyId }),
  savings: (days: number) => request<SavingsEstimate>("GET", `/api/usage/savings?days=${days}`),
  topSessions: () => request<TopSession[]>("GET", "/api/usage/top-sessions"),
  activity: () => request<{ start: string; end: string; buckets: HourBucket[]; requestsRead: number; total: number; truncated: boolean }>("GET", "/api/usage/activity"),
  adminKeys: () => request<AdminKey[]>("GET", "/api/admin/keys"),
  adminUsers: () => request<Array<UserSummary & { directory: DirectoryEntry | null }>>("GET", "/api/admin/users"),
  adminSetDirectory: (userId: string, e: { division: string; businessUnit: string; email: string | null }) =>
    request<DirectoryEntry>("PUT", `/api/admin/directory/${encodeURIComponent(userId)}`, e),
  adminRetag: (id: string, labels: { division: string; businessUnit: string; recordForOwner: boolean }) =>
    request<KeySummary>("POST", `/api/admin/keys/${id}/retag`, labels),
  adminAudit: () => request<AuditEvent[]>("GET", "/api/admin/audit"),
  adminTeams: () => request<AdminTeams>("GET", "/api/admin/teams"),
  adminServiceKeys: () => request<{ configured: boolean; keys: ServiceKey[] }>("GET", "/api/admin/service-keys"),
  adminCreateServiceKey: (r: ServiceKeyRequest) => request<{ key: string; summary: ServiceKey }>("POST", "/api/admin/service-keys", r),
  adminUpdateServiceKey: (id: string, r: Omit<ServiceKeyRequest, "name">) => request<ServiceKey>("PATCH", `/api/admin/service-keys/${id}`, r),
  adminRotateServiceKey: (id: string) => request<{ key: string; summary: ServiceKey }>("POST", `/api/admin/service-keys/${id}/rotate`),
  adminRevokeServiceKey: (id: string) => request<{ revoking: boolean }>("DELETE", `/api/admin/service-keys/${id}`),
};
